// Implements: F-UIX-020, F-UIX-010
/**
 * The profile listing, one profile, and the save, for the editor's containers.
 *
 * The keys are this module's own (`["design", ...]`): nothing outside `design`
 * reads or invalidates them yet. One profile is keyed by its id, so a slow
 * answer for profile A can only ever fill A's entry, never B's (checklist 14).
 *
 * The save never rejects: it answers the stored detail or the refusal, because
 * the container has one decision left (show the new version, or say why not
 * while keeping what the person typed).
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { useCallback } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import type {
  ProfileDetailView,
  ProfileListingView,
  ProfileSummaryView,
  ProfileVersionValues,
  ScopeKeyView,
  UploadAssetInput,
  UploadedAssetView,
} from "../../../types/design";
import {
  activateProfile,
  addProfileVersion,
  clearActiveProfile,
  createProfile,
  deleteProfile,
  duplicateProfile,
  fetchProfile,
  fetchProfileListing,
  renameProfile,
  uploadAsset,
} from "../api/designApi";

export const DESIGN_PROFILES_KEY = ["design", "profiles"] as const;

export function designProfileKey(profileId: string): readonly ["design", "profile", string] {
  return ["design", "profile", profileId];
}

export function useProfileListing(): UseQueryResult<ProfileListingView | undefined, ApiError> {
  return useQuery<ProfileListingView | undefined, ApiError>({
    queryKey: DESIGN_PROFILES_KEY,
    queryFn: fetchProfileListing,
  });
}

export function useProfile(
  profileId: string,
): UseQueryResult<ProfileDetailView | undefined, ApiError> {
  return useQuery<ProfileDetailView | undefined, ApiError>({
    queryKey: designProfileKey(profileId),
    queryFn: () => fetchProfile(profileId),
  });
}

/** What a save asks for: a new profile (with its name and scope) or a new version of one. */
export type SaveRequest =
  | {
      readonly kind: "create";
      readonly scope: ScopeKeyView;
      readonly name: string;
      readonly values: ProfileVersionValues;
    }
  | { readonly kind: "version"; readonly profileId: string; readonly values: ProfileVersionValues };

export type SaveOutcome =
  | { readonly ok: true; readonly detail: ProfileDetailView | undefined }
  | { readonly ok: false; readonly error: ApiError };

export function useSaveProfile(): (request: SaveRequest) => Promise<SaveOutcome> {
  const queryClient = useQueryClient();

  return useCallback(
    async (request: SaveRequest): Promise<SaveOutcome> => {
      let detail: ProfileDetailView | undefined;
      try {
        detail =
          request.kind === "create"
            ? await createProfile(request)
            : await addProfileVersion(request.profileId, request.values);
      } catch (cause) {
        return { ok: false, error: asApiError(cause) };
      }
      void queryClient.invalidateQueries({ queryKey: DESIGN_PROFILES_KEY });
      // The answer IS the stored profile at its new version: cache it under its own
      // id, so opening it shows it at once instead of a loading state and a refetch.
      if (detail !== undefined) queryClient.setQueryData(designProfileKey(detail.id), detail);
      return { ok: true, detail };
    },
    [queryClient],
  );
}

export type UploadOutcome =
  | { readonly ok: true; readonly uploaded: UploadedAssetView | undefined }
  | { readonly ok: false; readonly error: ApiError };

/**
 * Attach one image. The answer is a new version, so the profile (by its own id)
 * and the listing are read again; like the save, it never rejects.
 */
export function useUploadAsset(): (profileId: string, input: UploadAssetInput) => Promise<UploadOutcome> {
  const queryClient = useQueryClient();

  return useCallback(
    async (profileId: string, input: UploadAssetInput): Promise<UploadOutcome> => {
      let uploaded: UploadedAssetView | undefined;
      try {
        uploaded = await uploadAsset(profileId, input);
      } catch (cause) {
        return { ok: false, error: asApiError(cause) };
      }
      void queryClient.invalidateQueries({ queryKey: DESIGN_PROFILES_KEY });
      void queryClient.invalidateQueries({ queryKey: designProfileKey(profileId) });
      return { ok: true, uploaded };
    },
    [queryClient],
  );
}

export type ScopeOutcome = { readonly ok: true } | { readonly ok: false; readonly error: ApiError };

export interface ScopeActivation {
  readonly activate: (scope: ScopeKeyView, profileId: string) => Promise<ScopeOutcome>;
  readonly clear: (scope: ScopeKeyView) => Promise<ScopeOutcome>;
}

/**
 * Mark a profile active in a scope, or clear the mark (W2l). Like the save, it
 * never rejects. The listing is read again whatever the answer (a refusal may
 * mean the scope changed under the person), and so is every mockup's design:
 * the cascade it shows "now" moves, although no pin does (P6-OD-20).
 */
export function useScopeActivation(): ScopeActivation {
  const queryClient = useQueryClient();

  const run = useCallback(
    async (send: () => Promise<unknown>): Promise<ScopeOutcome> => {
      try {
        await send();
        return { ok: true };
      } catch (cause) {
        return { ok: false, error: asApiError(cause) };
      } finally {
        void queryClient.invalidateQueries({ queryKey: DESIGN_PROFILES_KEY });
        void queryClient.invalidateQueries({ queryKey: ["design", "mockup"] });
      }
    },
    [queryClient],
  );

  return {
    activate: useCallback((scope, profileId) => run(() => activateProfile(scope, profileId)), [run]),
    clear: useCallback((scope) => run(() => clearActiveProfile(scope)), [run]),
  };
}

export type RenameOutcome =
  | { readonly ok: true; readonly summary: ProfileSummaryView | undefined }
  | { readonly ok: false; readonly error: ApiError };

/**
 * Rename a profile in place (F-DSG-001, mockup v3 P5): no new version. Like the save, it never
 * rejects. The answer is written ONLY into the renamed profile's own entry (by its id, checklist 14),
 * so an answer that arrives after the person opened another profile cannot touch that one; the
 * listing is read again whatever the answer.
 */
export function useRenameProfile(): (profileId: string, name: string) => Promise<RenameOutcome> {
  const queryClient = useQueryClient();

  return useCallback(
    async (profileId: string, name: string): Promise<RenameOutcome> => {
      let summary: ProfileSummaryView | undefined;
      try {
        summary = await renameProfile(profileId, { name });
      } catch (cause) {
        return { ok: false, error: asApiError(cause) };
      } finally {
        void queryClient.invalidateQueries({ queryKey: DESIGN_PROFILES_KEY });
      }
      const renamed = summary?.name ?? name;
      queryClient.setQueryData<ProfileDetailView | undefined>(designProfileKey(profileId), (old) =>
        old === undefined ? old : { ...old, name: renamed },
      );
      return { ok: true, summary };
    },
    [queryClient],
  );
}

export type DuplicateOutcome =
  | { readonly ok: true; readonly copy: ProfileDetailView | undefined }
  | { readonly ok: false; readonly error: ApiError };

/**
 * "Duplicar y editar" (owner 2026-10-19, answer 4A): the server's instant copy. Like the save, it
 * never rejects; the listing is read again and the copy is cached under its own id, so it opens at
 * once with its images.
 */
export function useDuplicateProfile(): (profileId: string) => Promise<DuplicateOutcome> {
  const queryClient = useQueryClient();

  return useCallback(
    async (profileId: string): Promise<DuplicateOutcome> => {
      let copy: ProfileDetailView | undefined;
      try {
        copy = await duplicateProfile(profileId);
      } catch (cause) {
        return { ok: false, error: asApiError(cause) };
      }
      void queryClient.invalidateQueries({ queryKey: DESIGN_PROFILES_KEY });
      if (copy !== undefined) queryClient.setQueryData(designProfileKey(copy.id), copy);
      return { ok: true, copy };
    },
    [queryClient],
  );
}

export type DeleteOutcome = { readonly ok: true } | { readonly ok: false; readonly error: ApiError };

/**
 * Delete a profile (owner 2026-10-19): the server archives it. Like the save, it never rejects,
 * and the listing is read again whatever the answer, so a deleted profile leaves the list.
 */
export function useDeleteProfile(): (profileId: string) => Promise<DeleteOutcome> {
  const queryClient = useQueryClient();

  return useCallback(
    async (profileId: string): Promise<DeleteOutcome> => {
      try {
        await deleteProfile(profileId);
      } catch (cause) {
        return { ok: false, error: asApiError(cause) };
      } finally {
        void queryClient.invalidateQueries({ queryKey: DESIGN_PROFILES_KEY });
      }
      return { ok: true };
    },
    [queryClient],
  );
}
